import { useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { useEffect, useId, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/ui/avatar'
import { api } from '@/lib/api/client'
import { cn } from '@/lib/utils'

type Props = {
  id: string
  /** Virgülle ayrılmış adresler (taslak ve gönderim bu biçimi kullanır). */
  value: string
  onChange: (value: string) => void
  placeholder?: string
  autoFocus?: boolean
}

type Contact = { email: string; count: number }

const SEPARATORS = /[,;\s]+/
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const splitAddresses =(raw: string) => raw.split(/[,;]/).map((s) => s.trim()).filter(Boolean)

/** Gmail tarzı alıcı alanı: adresler çip olur, yazarken daha önce yazışılan kişiler önerilir. */
export function RecipientInput({ id, value, onChange, placeholder, autoFocus }: Props) {
  const { t } = useTranslation()
  const listId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [draft, setDraft] = useState('')
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const chips = splitAddresses(value)
  const chosen = new Set(chips.map((c) => c.toLowerCase()))

  // Her tuşta istek atılmasın.
  useEffect(() => {
    const id = window.setTimeout(() => setQuery(draft.trim()), 150)
    return () => window.clearTimeout(id)
  }, [draft])

  const contacts = useQuery({
    queryKey: ['mail', 'contacts', query],
    enabled: open,
    staleTime: 60_000,
    // Öneri yalnız kolaylık: alınamazsa alan düz adres girişi olarak çalışır, tekrar denenmez.
    retry: false,
    queryFn: () => api.get<Contact[]>('/api/v1/mail/contacts', { q: query || undefined, limit: 8 }),
  })
  const suggestions = (contacts.data ?? []).filter((c) => !chosen.has(c.email.toLowerCase()))
  const showList = open && suggestions.length > 0

  function commit(raw: string) {
    const next = raw.split(SEPARATORS).map((s) => s.trim()).filter((s) => s && !chosen.has(s.toLowerCase()))
    if (next.length) onChange([...chips, ...next].join(', '))
    setDraft('')
    setActive(0)
  }
  function remove(index: number) {
    onChange(chips.filter((_, i) => i !== index).join(', '))
    inputRef.current?.focus()
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!open && e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      return
    }
    if (showList && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault()
      setActive((i) => (i + (e.key === 'ArrowDown' ? 1 : suggestions.length - 1)) % suggestions.length)
      return
    }
    if (e.key === 'Enter' || (e.key === 'Tab' && (draft.trim() || showList))) {
      if (showList && suggestions[active]) {
        e.preventDefault()
        commit(suggestions[active].email)
        return
      }
      if (draft.trim()) {
        e.preventDefault()
        commit(draft)
      } else if (e.key === 'Enter') e.preventDefault()
      return
    }
    if (e.key === ',' || e.key === ';' || e.key === ' ') {
      if (draft.trim()) {
        e.preventDefault()
        commit(draft)
      }
      return
    }
    if (e.key === 'Backspace' && !draft && chips.length) {
      e.preventDefault()
      remove(chips.length - 1)
      return
    }
    if (e.key === 'Escape' && open) {
      e.stopPropagation()
      setOpen(false)
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text')
    if (!SEPARATORS.test(text.trim())) return
    e.preventDefault()
    commit(`${draft} ${text}`)
  }

  return (
    <div className="relative min-w-0 flex-1">
      <div className="flex min-h-9 flex-wrap items-center gap-1 py-1" onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault()
          inputRef.current?.focus()
          setOpen(true)
        }
      }}>
        {chips.map((address, i) => {
          const valid = EMAIL.test(address)
          return (
            <span
              key={`${address}-${i}`}
              title={valid ? address : t('mail.mailbox.recipients.invalid')}
              className={cn(
                'inline-flex h-6 max-w-full items-center gap-1 rounded-full border pl-2 pr-1 text-xs',
                valid ? 'border-border bg-muted/60 text-foreground' : 'border-destructive/40 bg-destructive/10 text-destructive',
              )}
            >
              <span className="truncate">{address}</span>
              <button
                type="button"
                className="flex size-4 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-foreground/10 hover:text-foreground"
                aria-label={t('mail.mailbox.recipients.remove', { address })}
                onClick={() => remove(i)}
              >
                <X className="size-3" />
              </button>
            </span>
          )
        })}
        <input
          ref={inputRef}
          id={id}
          value={draft}
          autoFocus={autoFocus}
          autoComplete="off"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          placeholder={chips.length ? undefined : placeholder}
          className="h-7 min-w-[8rem] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          onChange={(e) => {
            setDraft(e.target.value)
            setActive(0)
            setOpen(true)
          }}
          // Öneriler odakla değil kullanıcı eylemiyle açılır: pencere açılırken alan otomatik odaklanır,
          // liste kendiliğinden açılmamalı.
          onClick={() => setOpen(true)}
          onBlur={() => {
            setOpen(false)
            if (draft.trim()) commit(draft)
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
      </div>
      {showList ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-xl border border-border bg-popover p-1 shadow-lg"
        >
          {suggestions.map((c, i) => (
            <li
              key={c.email}
              role="option"
              aria-selected={i === active}
              className={cn('flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm', i === active ? 'bg-muted' : 'hover:bg-muted/60')}
              // Tıklama input'un blur'undan önce işlensin.
              onMouseDown={(e) => {
                e.preventDefault()
                commit(c.email)
              }}
              onMouseEnter={() => setActive(i)}
            >
              <Avatar name={c.email} className="size-7" />
              <span className="min-w-0 flex-1 truncate">{c.email}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
